import { connect } from 'react-redux';
import { compose } from 'redux';
import { clearSubmitErrors, initialize, reduxForm, SubmissionError } from 'redux-form';

import request from 'common/Api';
import { APP_PAGES, B2C_USER_TYPES, EDIT_FORM_TYPE, EDIT_NAME_STEPS, FORMS } from 'common/Constants';
import { receiveAzure, setB2CUpdateStatus, setPassengerUpdateStatus } from 'common/SearchStore/SearchActions';
import { getAzureData } from 'common/SearchStore/SearchSelectors';
import { updateEditNames, updateEditNameStep, updateSelectedBookingPassenger } from 'common/UserStore/UserActions';
import {
  getBookingId,
  getCruiseName,
  getCSAUserAuthData,
  getEditNameFormStep,
  getEditNames,
} from 'common/UserStore/UserSelectors';
import { base64Encode } from 'utils/strings';
import EditName from './EditName';

const mapStateToProps = (state) => {
  const azureData = getAzureData(state);
  const { email = '', firstName = '', lastName = '', userType = 'Consumer' } = azureData || {};
  return {
    initialValues: {
      email: '',
      firstName,
      lastName,
      userType,
    },
    email,
    userType,
    formStep: getEditNameFormStep(state) || EDIT_NAME_STEPS.CONFIRM_EMAIL,
    adminEmail: getCSAUserAuthData(state)?.attributes?.email,
    editNames: getEditNames(state),
    cruiseName: getCruiseName(state),
    bookingId: getBookingId(state),
    userTypeOptions: Object.keys(B2C_USER_TYPES).map((key) => ({
      label: B2C_USER_TYPES[key]?.label || key,
      selected: key === userType,
      value: B2C_USER_TYPES[key]?.value || key,
    })),
  };
};

const mapDispatchToProps = (dispatch) => ({
  onClose: () => {
    dispatch(initialize(FORMS.EDIT_NAME));
    dispatch(receiveAzure({}));
    dispatch(updateEditNameStep(EDIT_NAME_STEPS.CONFIRM_EMAIL));
    dispatch(updateSelectedBookingPassenger({ passengerNumber: null, cruiseName: null, bookingId: null }));
  },
});

const onSubmit = ({ ...values }, dispatch, { adminEmail, email, formStep, user }) =>
  new Promise((resolve, reject) => {
    const workflow = window.location.pathname === APP_PAGES.USERS.path ? 'user' : 'booking';
    dispatch(clearSubmitErrors(FORMS.EDIT_NAME));
    if (formStep === EDIT_NAME_STEPS.CONFIRM_EMAIL) {
      if (adminEmail === values.email) {
        const formError = { _error: "Please enter the user's email address." };
        return reject(new SubmissionError(formError));
      }
      if (user?.email && (user.email || '').trim().toLowerCase() !== (values?.email || '').trim().toLowerCase()) {
        const formError = { _error: "Email does not match. Please confirm the user's email address." };
        return reject(new SubmissionError(formError));
      }
      const encodedEmail = base64Encode(values.email.toLowerCase());
      return dispatch(request({ method: 'GET', url: `/csp/user/${encodedEmail}` }))
        .then((authUserData) => {
          const {
            customUserType: userType,
            emailAddress,
            givenName: firstName,
            id,
            surname: lastName,
            userPrincipalName,
          } = authUserData?.authData || {};
          const userData = { email: emailAddress, firstName, id, lastName, userPrincipalName, userType };
          if (!userData?.email) {
            const formError = 'No matching MVJ Accounts found. Please advise the guest to create a new MVJ account.';
            return reject(new SubmissionError({ _error: formError }));
          }
          dispatch(
            receiveAzure({
              ...userData,
              email: values.email,
            }),
          );
          dispatch(updateEditNameStep(EDIT_NAME_STEPS.EDIT));
          return null;
        })
        .catch(({ response }) => {
          let formError = 'No matching MVJ Accounts found. Please advise the guest to create a new MVJ account.';
          if (response.status === 503) {
            formError =
              'My Viking Journey is currently down for maintenance and will be back soon. ' +
              '<br /> Please try again later.';
          }
          return reject(new SubmissionError({ _error: formError }));
        });
    }

    if (formStep === EDIT_NAME_STEPS.EDIT) {
      dispatch(updateEditNames(values));
      dispatch(updateEditNameStep(EDIT_NAME_STEPS.CONFIRM_EDIT));
      return resolve(values);
    }

    if (formStep === EDIT_NAME_STEPS.CONFIRM_EDIT) {
      const encodedEmail = btoa(email.toLowerCase());
      return dispatch(
        request({
          method: 'PUT',
          url: `/csp/${encodedEmail}/${user.firstName}/${user.lastName}?workflow=${workflow}`,
          params: {
            userId: '',
            updateData: {
              firstName: values.firstName,
              lastName: values.lastName,
              userType: values.userType,
            },
          },
        }),
      )
        .then(() => {
          dispatch(updateEditNames({}));
          const formType = window.location?.pathname?.startsWith(APP_PAGES.USERS.path)
            ? EDIT_FORM_TYPE.B2C_USER
            : EDIT_FORM_TYPE.MVJ_USER;
          if (formType === EDIT_FORM_TYPE.MVJ_USER) {
            dispatch(setPassengerUpdateStatus(user.pax, "Guest name updated in user's My Viking Journey account."));
          } else {
            dispatch(setB2CUpdateStatus('B2C account data was successfully updated.'));
          }
          window.$(`#${FORMS.EDIT_NAME}`).modal('hide');
          return resolve();
        })
        .catch(() =>
          reject(
            new SubmissionError({
              _error: 'Guest names failed to update. Please try again.',
            }),
          ),
        );
    }

    return reject('Unknown Edit Name Step');
  });

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({
    form: FORMS.EDIT_NAME,
    enableReinitialize: true,
    onSubmit,
  }),
)(EditName);

export default enhance;
